import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  FormGroup,
  FormFeedback,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { AuthButton, AuthInput } from "../../auth/style";
import { setAuthenticatedUser, getCurrentUser } from "../../auth/service";
import storeApi from "../storeApi";
import { AppContext } from "../../context";
import { clearStoreSession } from "../storeSession";

/**
 * Route: /store/register
 * - Full registration: OTP then verify-otp.
 * - postLoginMode: logged-in user; PATCH profile (company + business type).
 */
const PROJECT_TYPE_OPTIONS = [
  { value: "interior_designer", label: "Interior Designer" },
  { value: "kitchen_company", label: "Kitchen Company" },
  { value: "architect", label: "Architect" },
  { value: "contractor", label: "Contractor" },
  { value: "carpenter", label: "Carpenter" },
  { value: "vendor", label: "Vendor" },
  { value: "oems", label: "OEMs" },
  { value: "builder", label: "Builder" },
  { value: "home_owner", label: "Home Owner" },
];

const StoreRegister = ({
  isOpen = true,
  onClose = null,
  onSwitchToLogin = null,
  onOtpGenerated = null,
  postLoginMode = false,
  initialData = null,
  onProfileComplete = null,
  titleOverride = null,
  hideLoginLink = false,
  verifyRoute = "/verify-otp",
  redirectAuthenticatedTo = "/projects",
  userType = "buyer",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    setCurrentUser,
    popAuthModalReturnTarget,
    closeAuthModal,
  } = useContext(AppContext) || {};

  const fromLocationPostLogin = Boolean(
    location?.state?.postLoginProfile
  );
  const isPostLogin = postLoginMode || fromLocationPostLogin;

  const isTempGuestUser =
    Boolean(currentUser?.temp === true) ||
    String(currentUser?.mobile ?? "")
      .toLowerCase()
      .startsWith("guest_");

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [projectType, setProjectType] = useState("");
  const [isGeneratingOtp, setIsGeneratingOtp] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [error, setError] = useState(null);
  const [profileModalDismissed, setProfileModalDismissed] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setProfileModalDismissed(false);
      setShowValidationErrors(false);
    }
  }, [isOpen]);

  const toErrorText = (err, fallback) => {
    const raw =
      err?.message ??
      err?.error ??
      err?.response?.data?.message ??
      err?.response?.data?.error ??
      fallback;
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object") {
      if (typeof raw.message === "string") return raw.message;
      if (typeof raw.error === "string") return raw.error;
      try {
        return JSON.stringify(raw);
      } catch {
        return fallback;
      }
    }
    return String(raw ?? fallback);
  };

  useEffect(() => {
    const seed = initialData || (fromLocationPostLogin ? location.state : null);
    if (!seed) return;
    setName(String(seed.name ?? ""));
    setMobile(String(seed.mobile ?? ""));
    setCompanyName(String(seed.companyName ?? ""));
    const pt = seed.projectType;
    setProjectType(
      typeof pt === "string" && pt ? pt : pt?.value ? String(pt.value) : ""
    );
  }, [initialData, fromLocationPostLogin, location.state]);

  useEffect(() => {
    if (isPostLogin && !mobile && currentUser?.mobile) {
      setMobile(String(currentUser.mobile));
    }
  }, [isPostLogin, mobile, currentUser]);

  useEffect(() => {
    if (isPostLogin) return;
    if (currentUser && !isTempGuestUser) {
      navigate(redirectAuthenticatedTo, { replace: true });
    }
  }, [currentUser, isTempGuestUser, navigate, isPostLogin, redirectAuthenticatedTo]);

  const isValidMobile = (value) => value.length === 10 && /^\d+$/.test(value);
  const isValidName = (value) => value.trim().length >= 2;
  const isValidCompanyName = (value) => value.trim().length >= 2;
  const isValidBusinessType = (value) =>
    PROJECT_TYPE_OPTIONS.some((opt) => opt.value === value);

  const canGenerateOtp =
    !isPostLogin &&
    isValidName(name) &&
    isValidCompanyName(companyName) &&
    isValidMobile(mobile) &&
    isValidBusinessType(projectType) &&
    !isGeneratingOtp;

  const dismissProfileModal = () => {
    setProfileModalDismissed(true);
    if (typeof closeAuthModal === "function") closeAuthModal();
    if (typeof onProfileComplete === "function") onProfileComplete();
    if (typeof onClose === "function") onClose();
  };

  const canSavePostLoginProfile =
    isPostLogin &&
    !isSavingProfile &&
    isValidBusinessType(projectType) &&
    isValidCompanyName(companyName);

  const handlePostLoginSave = async (event) => {
    event.preventDefault();
    if (!canSavePostLoginProfile) {
      setShowValidationErrors(true);
      return;
    }
    setIsSavingProfile(true);
    setError(null);
    try {
      const body = {
        company_name: companyName.trim(),
        profile_type: projectType,
      };
      const res = await storeApi.authAPI.updateProfile(body);
      const root = res || {};
      const inner = root.data || {};
      if (inner.account_unsupported) {
        clearStoreSession();
        if (typeof setCurrentUser === "function") setCurrentUser(null);
        dismissProfileModal();
        navigate("/account-not-supported", { replace: true });
        return;
      }
      const u = inner.user || root.user;
      const token = inner.token || localStorage.getItem("storeAccessToken");
      if (u) {
        setAuthenticatedUser({ token: token || "", user: u });
        setCurrentUser(getCurrentUser());
      }
      dismissProfileModal();
      const dest =
        typeof popAuthModalReturnTarget === "function"
          ? popAuthModalReturnTarget()
          : null;
      if (dest?.pathname) {
        navigate(dest.pathname, {
          replace: true,
          ...(dest.state !== undefined ? { state: dest.state } : {}),
        });
      } else {
        navigate("/products", { replace: true });
      }
    } catch (e) {
      setError(toErrorText(e, "Unable to save profile."));
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleGenerateOtp = async (event) => {
    event.preventDefault();
    if (!canGenerateOtp) {
      setShowValidationErrors(true);
      return;
    }
    setIsGeneratingOtp(true);
    setError(null);
    try {
      await storeApi.authAPI.generateOtpForLogin({
        mobile: mobile.trim(),
        is_registration: true,
      });
      window.sessionStorage.setItem(
        "zb_store_registration",
        JSON.stringify({
          name: name.trim(),
          mobile: mobile.trim(),
          companyName: companyName.trim(),
          projectType,
          userType,
        })
      );
      const payload = {
        mobile: mobile.trim(),
        name: name.trim(),
        companyName: companyName.trim(),
        projectType,
        userType,
      };
      if (typeof onOtpGenerated === "function") {
        onOtpGenerated(payload);
        return;
      }
      navigate(verifyRoute, { state: payload });
    } catch (e) {
      setError(toErrorText(e, "Unable to send OTP. Please try again."));
    } finally {
      setIsGeneratingOtp(false);
    }
  };

  const handleClose = () => {
    if (isPostLogin) return;
    if (typeof onClose === "function") {
      onClose();
      return;
    }
    if (typeof window !== "undefined") window.history.back();
  };

  const title = isPostLogin
    ? "Complete Your Profile"
    : (titleOverride || "Create Account");

  const modalToggle = isPostLogin ? undefined : handleClose;
  const modalIsOpen =
    isPostLogin ? isOpen && !profileModalDismissed : isOpen;

  const showBusinessTypeError =
    showValidationErrors && !isValidBusinessType(projectType);
  const showCompanyNameError =
    showValidationErrors && !isValidCompanyName(companyName);

  const businessTypePills = (
    <FormGroup noMargin className="mb-4">
      <div className="small text-muted mb-2">
        Business type <span className="text-danger">*</span>
      </div>
      <div className="d-flex flex-wrap gap-2">
        {PROJECT_TYPE_OPTIONS.map((opt) => {
          const active = projectType === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              className={
                active
                  ? "btn btn-sm btn-primary rounded-pill"
                  : "btn btn-sm btn-outline-secondary rounded-pill"
              }
              onClick={() => setProjectType(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {showBusinessTypeError ? (
        <div className="text-danger small mt-2">
          Please select a business type.
        </div>
      ) : null}
    </FormGroup>
  );

  return (
    <Modal
      isOpen={modalIsOpen}
      centered={false}
      toggle={modalToggle}
      size="md"
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader toggle={isPostLogin ? false : handleClose}>
        {title}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={isPostLogin ? handlePostLoginSave : handleGenerateOtp}>
          {!isPostLogin && (
            <FormGroup noMargin className="mb-4">
              <AuthInput
                invalid={name.length > 0 && !isValidName(name)}
                type="text"
                value={name}
                placeholder="Your Name"
                onChange={(e) => setName(e.target.value)}
                className="mt-2 unmask"
              />
              <FormFeedback>
                Name must contain at least 2 characters.
              </FormFeedback>
            </FormGroup>
          )}

          {!isPostLogin && (
            <FormGroup noMargin className="mb-4">
              <AuthInput
                invalid={mobile.length > 0 && !isValidMobile(mobile)}
                type="tel"
                value={mobile}
                placeholder="Mobile Number"
                onChange={(e) => setMobile(e.target.value)}
                className="unmask"
              />
              <FormFeedback>
                Please enter a valid 10 digit mobile number.
              </FormFeedback>
              <div className="d-block mt-2 pb-1 text-subtle">
                OTP will be sent via WhatsApp
              </div>
            </FormGroup>
          )}

          <FormGroup noMargin className="mb-4">
            <div className="small text-muted mb-2">
              Company <span className="text-danger">*</span>
            </div>
            <AuthInput
              required
              invalid={
                showCompanyNameError ||
                (companyName.length > 0 && !isValidCompanyName(companyName))
              }
              type="text"
              value={companyName}
              placeholder="Your Company / Business Name"
              onChange={(e) => setCompanyName(e.target.value)}
              className="unmask mt-0"
            />
            <FormFeedback>
              Company name is required (at least 2 characters).
            </FormFeedback>
          </FormGroup>

          {businessTypePills}

          {error && <Alert color="danger">{error} &nbsp;</Alert>}

          <div className="d-grid gap-2">
            {isPostLogin ? (
              <AuthButton
                type="submit"
                disabled={!canSavePostLoginProfile}
                color="primary"
              >
                {isSavingProfile ? (
                  <>
                    <Spinner size="sm">Loading...</Spinner> &nbsp;
                    <span>Saving...</span>
                  </>
                ) : (
                  "Continue"
                )}
              </AuthButton>
            ) : (
              <AuthButton
                type="submit"
                disabled={!canGenerateOtp}
                color="primary"
              >
                {isGeneratingOtp ? (
                  <>
                    <Spinner size="sm">Loading...</Spinner> &nbsp;
                    <span>Sending OTP...</span>
                  </>
                ) : (
                  "Generate OTP"
                )}
              </AuthButton>
            )}
          </div>

          {!isPostLogin && !hideLoginLink ? (
            <p className="text-center mt-3 mb-0">
              Already have an account? &nbsp;
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none"
                onClick={() => {
                  if (typeof onSwitchToLogin === "function") {
                    onSwitchToLogin();
                    return;
                  }
                  navigate("/login");
                }}
              >
                Login here
              </button>
            </p>
          ) : null}
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default StoreRegister;
